'use client';

import React, { createContext, useContext, useEffect, useState } from 'react';
import { translations, type Lang, type Translations } from '@/lib/i18n/translations';
import { resolveInitialLang, setStoredLang } from '@/lib/i18n/resolveLang';

interface LanguageContextValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
  t: Translations;
  // False until the client-side mount effect has resolved the real
  // language from localStorage. Screens that want to avoid a flash of the
  // default language before the true preference loads can gate on this,
  // the same way this codebase already gates theme-dependent rendering.
  mounted: boolean;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>('sq');
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setLangState(resolveInitialLang());
    setMounted(true);
  }, []);

  useEffect(() => {
    if (mounted) {
      document.documentElement.lang = lang;
    }
  }, [lang, mounted]);

  const setLang = (next: Lang) => {
    setLangState(next);
    setStoredLang(next);
  };

  const value: LanguageContextValue = {
    lang,
    setLang,
    t: translations[lang],
    mounted,
  };

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage(): LanguageContextValue {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
